import { FormattedMessage, useIntl } from 'react-intl';

import BooleanBadge from 'src/features/membership/components/BooleanBadge';
import { EventType, operationalErrorMap } from 'src/features/payment/utils/constants';
import { paymentsMessages } from 'src/features/payment/utils/messages';
import {
    EventMeta1,
    EventMeta2,
    EventMeta4,
    EventMeta6,
    EventMeta7,
    EventMetaCorrections,
    PayStationEvent,
} from 'src/features/payment/utils/types';
import {
    SidePanelBodyAttribute,
    SidePanelBodyAttributeBody,
    SidePanelBodyAttributeTitle,
} from 'src/registry/components/sidePanel/SidePanelBody';
import { DialogSeparator, DialogTitle } from 'src/registry/ui/Dialog';
import { generalMessages } from 'src/registry/utils/messages';

const EventMetaObj1 = ({ meta: { event_objects } }: { meta: EventMeta1 }) => {
    const { formatNumber } = useIntl();
    return event_objects.map(({ Count, Value, Currency, DeviceType, ReferenceNumber, InRecycleBox }, index) => (
        <>
            <DialogSeparator className="m-4" />
            <DialogTitle className="text-base">
                <FormattedMessage {...paymentsMessages.object} /> {index + 1}
            </DialogTitle>
            <SidePanelBodyAttribute>
                <SidePanelBodyAttributeTitle>
                    <FormattedMessage {...paymentsMessages.count} />
                </SidePanelBodyAttributeTitle>
                <SidePanelBodyAttributeBody control={Count}>{Count}</SidePanelBodyAttributeBody>
            </SidePanelBodyAttribute>
            <SidePanelBodyAttribute>
                <SidePanelBodyAttributeTitle>
                    <FormattedMessage {...paymentsMessages.amount} />
                </SidePanelBodyAttributeTitle>
                <SidePanelBodyAttributeBody control={Value}>
                    {Value &&
                        formatNumber(Value / 100, {
                            style: 'currency',
                            currency: Currency,
                        })}
                </SidePanelBodyAttributeBody>
            </SidePanelBodyAttribute>
            <SidePanelBodyAttribute>
                <SidePanelBodyAttributeTitle>
                    <FormattedMessage {...paymentsMessages.currency} />
                </SidePanelBodyAttributeTitle>
                <SidePanelBodyAttributeBody control={Currency}>{Currency}</SidePanelBodyAttributeBody>
            </SidePanelBodyAttribute>
            <SidePanelBodyAttribute>
                <SidePanelBodyAttributeTitle>
                    <FormattedMessage {...paymentsMessages.deviceType} />
                </SidePanelBodyAttributeTitle>
                <SidePanelBodyAttributeBody control={DeviceType}>{DeviceType}</SidePanelBodyAttributeBody>
            </SidePanelBodyAttribute>
            <SidePanelBodyAttribute>
                <SidePanelBodyAttributeTitle>
                    <FormattedMessage {...paymentsMessages.inRecycleBox} />
                </SidePanelBodyAttributeTitle>
                <BooleanBadge booleanField={InRecycleBox} />
            </SidePanelBodyAttribute>
            <SidePanelBodyAttribute>
                <SidePanelBodyAttributeTitle>
                    <FormattedMessage {...paymentsMessages.reference} />
                </SidePanelBodyAttributeTitle>
                <SidePanelBodyAttributeBody control={ReferenceNumber}>{ReferenceNumber}</SidePanelBodyAttributeBody>
            </SidePanelBodyAttribute>
        </>
    ));
};

const EventMetaObj2 = ({ meta }: { meta: EventMeta2 }) => {
    const { device_type, address, operational_errors } = meta;
    return (
        <>
            <SidePanelBodyAttribute>
                <SidePanelBodyAttributeTitle>
                    <FormattedMessage {...paymentsMessages.address} />
                </SidePanelBodyAttributeTitle>
                <SidePanelBodyAttributeBody control={address}>{address}</SidePanelBodyAttributeBody>
            </SidePanelBodyAttribute>
            <SidePanelBodyAttribute>
                <SidePanelBodyAttributeTitle>
                    <FormattedMessage {...paymentsMessages.deviceType} />
                </SidePanelBodyAttributeTitle>
                <SidePanelBodyAttributeBody control>{device_type}</SidePanelBodyAttributeBody>
            </SidePanelBodyAttribute>
            <SidePanelBodyAttribute>
                <SidePanelBodyAttributeTitle>
                    <FormattedMessage {...paymentsMessages.operationalErrors} />
                </SidePanelBodyAttributeTitle>
                <SidePanelBodyAttributeBody control={operational_errors} className="flex flex-col gap-2">
                    {operational_errors.map((error) => (
                        <span key={error}>
                            <FormattedMessage {...operationalErrorMap[error]} />
                        </span>
                    ))}
                </SidePanelBodyAttributeBody>
            </SidePanelBodyAttribute>
        </>
    );
};

const EventMetaObj4 = ({ meta }: { meta: EventMeta4 }) => {
    if (typeof meta.error_details === 'string') return null;
    const { default_detail, default_code, status_code, name } = meta.error_details;

    return (
        <>
            <SidePanelBodyAttribute>
                <SidePanelBodyAttributeTitle>
                    <FormattedMessage {...generalMessages.name} />
                </SidePanelBodyAttributeTitle>
                <SidePanelBodyAttributeBody control={name}>{name}</SidePanelBodyAttributeBody>
            </SidePanelBodyAttribute>
            <SidePanelBodyAttribute>
                <SidePanelBodyAttributeTitle>
                    <FormattedMessage {...paymentsMessages.defaultCode} />
                </SidePanelBodyAttributeTitle>
                <SidePanelBodyAttributeBody control>{default_code}</SidePanelBodyAttributeBody>
            </SidePanelBodyAttribute>
            <SidePanelBodyAttribute>
                <SidePanelBodyAttributeTitle>
                    <FormattedMessage {...paymentsMessages.defaultDetail} />
                </SidePanelBodyAttributeTitle>
                <SidePanelBodyAttributeBody control={default_detail}>{default_detail}</SidePanelBodyAttributeBody>
            </SidePanelBodyAttribute>
            <SidePanelBodyAttribute>
                <SidePanelBodyAttributeTitle>
                    <FormattedMessage {...paymentsMessages.statusCode} />
                </SidePanelBodyAttributeTitle>
                <SidePanelBodyAttributeBody control={status_code}>{status_code}</SidePanelBodyAttributeBody>
            </SidePanelBodyAttribute>
        </>
    );
};

const EventMetaObj6 = ({ meta: { currency, denomination, count, in_recycle_box } }: { meta: EventMeta6 }) => (
    <>
        <SidePanelBodyAttribute>
            <SidePanelBodyAttributeTitle>
                <FormattedMessage {...paymentsMessages.count} />
            </SidePanelBodyAttributeTitle>
            <SidePanelBodyAttributeBody control={count}>{count}</SidePanelBodyAttributeBody>
        </SidePanelBodyAttribute>
        <SidePanelBodyAttribute>
            <SidePanelBodyAttributeTitle>
                <FormattedMessage {...paymentsMessages.denomination} />
            </SidePanelBodyAttributeTitle>
            <SidePanelBodyAttributeBody control={denomination}>{denomination}</SidePanelBodyAttributeBody>
        </SidePanelBodyAttribute>
        <SidePanelBodyAttribute>
            <SidePanelBodyAttributeTitle>
                <FormattedMessage {...paymentsMessages.currency} />
            </SidePanelBodyAttributeTitle>
            <SidePanelBodyAttributeBody control>{currency}</SidePanelBodyAttributeBody>
        </SidePanelBodyAttribute>
        <SidePanelBodyAttribute>
            <SidePanelBodyAttributeTitle>
                <FormattedMessage {...paymentsMessages.inRecycleBox} />
            </SidePanelBodyAttributeTitle>
            <BooleanBadge booleanField={in_recycle_box} />
        </SidePanelBodyAttribute>
    </>
);

const EventMetaObj7 = ({ meta: { transaction_number, currency, amount, type } }: { meta: EventMeta7 }) => {
    const { formatNumber } = useIntl();
    return (
        <>
            <SidePanelBodyAttribute>
                <SidePanelBodyAttributeTitle>
                    <FormattedMessage {...paymentsMessages.amount} />
                </SidePanelBodyAttributeTitle>
                <SidePanelBodyAttributeBody control={amount}>
                    {formatNumber(amount / 100, {
                        style: 'currency',
                        currency: currency,
                    })}
                </SidePanelBodyAttributeBody>
            </SidePanelBodyAttribute>
            <SidePanelBodyAttribute>
                <SidePanelBodyAttributeTitle>
                    <FormattedMessage {...paymentsMessages.transactionNumber} />
                </SidePanelBodyAttributeTitle>
                <SidePanelBodyAttributeBody control={transaction_number}>
                    {transaction_number}
                </SidePanelBodyAttributeBody>
            </SidePanelBodyAttribute>
            <SidePanelBodyAttribute>
                <SidePanelBodyAttributeTitle>
                    <FormattedMessage {...paymentsMessages.currency} />
                </SidePanelBodyAttributeTitle>
                <SidePanelBodyAttributeBody control={currency}>{currency}</SidePanelBodyAttributeBody>
            </SidePanelBodyAttribute>
            <SidePanelBodyAttribute>
                <SidePanelBodyAttributeTitle>
                    <FormattedMessage {...paymentsMessages.type} />
                </SidePanelBodyAttributeTitle>
                <SidePanelBodyAttributeBody control={type}>{type}</SidePanelBodyAttributeBody>
            </SidePanelBodyAttribute>
        </>
    );
};

const EventMetaObjCorrections = ({ meta: { corrections } }: { meta: EventMetaCorrections }) => {
    const { formatNumber } = useIntl();

    if (!corrections?.length) return null;

    return (
        <>
            <DialogSeparator className="m-4" />
            <DialogTitle className="text-base">
                <FormattedMessage {...paymentsMessages.correctedLevels} />
            </DialogTitle>
            {corrections.map(({ currency, value, old_count, new_count }) => (
                <>
                    <SidePanelBodyAttribute>
                        <SidePanelBodyAttributeTitle>
                            <FormattedMessage {...paymentsMessages.denomination} />
                        </SidePanelBodyAttributeTitle>
                        <SidePanelBodyAttributeBody control={value != null}>
                            {formatNumber(Number(value), { style: 'currency', currency })}
                        </SidePanelBodyAttributeBody>
                    </SidePanelBodyAttribute>
                    <SidePanelBodyAttribute>
                        <SidePanelBodyAttributeTitle>
                            <FormattedMessage {...paymentsMessages.previousCount} />
                        </SidePanelBodyAttributeTitle>
                        <SidePanelBodyAttributeBody control={old_count != null}>{old_count}</SidePanelBodyAttributeBody>
                    </SidePanelBodyAttribute>
                    <SidePanelBodyAttribute>
                        <SidePanelBodyAttributeTitle>
                            <FormattedMessage {...paymentsMessages.correctedCount} />
                        </SidePanelBodyAttributeTitle>
                        <SidePanelBodyAttributeBody control={new_count != null}>{new_count}</SidePanelBodyAttributeBody>
                    </SidePanelBodyAttribute>
                </>
            ))}
        </>
    );
};

export default function EventMetaDataMap({ type, meta }: { type: EventType; meta: PayStationEvent['meta'] }) {
    if (type === EventType.SYNCHRONIZE_NOMINAL_CORRECTED_FROM_DEVICE)
        return <EventMetaObjCorrections meta={meta as EventMetaCorrections} />;
    if (type <= EventType.UNKNOWN_EVENT) return <EventMetaObj1 meta={meta as EventMeta1} />;
    else if (type <= EventType.OPERATIONAL_ERRORS_CHANGE) return <EventMetaObj2 meta={meta as EventMeta2} />;
    else if (type <= EventType.CASH_DISPENSE) return <EventMetaObj6 meta={meta as EventMeta6} />;
    else if (type <= EventType.FAILED_CARD_TRANSACTION_REVERSAL) return <EventMetaObj7 meta={meta as EventMeta7} />;
    else if (type <= EventType.CANCEL_OPERATION) return null;
    else if (type <= EventType.CORE_ERROR) return <EventMetaObj4 meta={meta as EventMeta4} />;
    else if (type <= EventType.RETRIEVE_ACTIVE_STAY)
        return null; // We skip the json
    else if (type <= EventType.TRANSFER_BANKNOTES_FROM_RECYCLE_BOX_CORE_ERROR)
        return <EventMetaObj4 meta={meta as EventMeta4} />;
}
